import { ColumnTable, Table } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col } from 'reactstrap';
import SectionTitle from '../../../../../../../components/Text/SectionTitle';
import TableEmptyContent from '../../../../../../../components/Text/TableEmptyContent';
import { TranslationsKeys } from '../../../../../../../locales/translations';
import { formatDate } from '../../../../../../../utils/dates';
import {
  EducationalInformationLoaderQuery,
  OtherStudiesPagination,
  OtherStudy,
} from '../../types';
import { ITEMS_PER_PAGE_DEFAULT } from './OtherStudiesLoader';

interface OtherStudiesProps {
  otherStudies: OtherStudy[];
  paginationData: OtherStudiesPagination;
  query: EducationalInformationLoaderQuery;
  updateQuery: (query: EducationalInformationLoaderQuery) => void;
  refresh: () => Promise<void>;
}
export const OtherStudies = ({
  otherStudies,
  paginationData,
  query,
  updateQuery,
}: OtherStudiesProps) => {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_FILE_EDUCATIONAL_BACKGROUND,
  );

  const texts = useMemo(() => {
    return {
      title: t(`secondaryTitle`),
      secondaryTitle: t(`secondaryTitle`),
      noRecords: t(`emptyContent.title`),
      noRecordsSubtitle: t(`emptyContent.subtitle`),
      columns: {
        academicLevel: t(`columns.academicLevel`),
        program: t(`columns.program`),
        description: t(`columns.description`),
        country: t(`columns.country`),
        institution: t(`columns.institution`),
        validation: t(`columns.validation`),
        dateOfAcquisition: t(`columns.dateOfAcquisition`),
        action: t(`columns.action`),
      },
      buttonText: t(`buttonText`),
      deleteSuccess: t(`form.deleteSuccess`),
      deleteError: t(`form.deleteError`),
    };
  }, [t]);

  const columns = useMemo<ColumnTable<OtherStudy>[]>(() => {
    const array: ColumnTable[] = [
      {
        headerText: texts.columns.academicLevel,
        columnName: 'academicLevel',
        tdClassName: 'text-center',
        thClassName: 'text-center',
      },
      {
        headerText: texts.columns.program,
        columnName: 'programName',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.columns.description,
        columnName: 'description',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.columns.country,
        columnName: 'country',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.columns.institution,
        columnName: 'institution',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.columns.validation,
        columnName: 'validated',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat({ value }) {
          return value ? 'Si' : 'No';
        },
      },
      {
        headerText: texts.columns.dateOfAcquisition,
        columnName: 'graduationDate',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat({ value }) {
          return formatDate(value, 'DD/MM/YYYY');
        },
      },
    ];

    return array;
  }, [texts]);

  return (
    <>
      <div className="w-100 mt-4">
        <Col xs={12} md={6} className="mb-4 pl-0">
          <SectionTitle text={texts.secondaryTitle} />
        </Col>

        <Table
          columns={columns}
          data={otherStudies}
          pagination={{
            totalItems: paginationData.totalItems,
            onChangePage: (page) => updateQuery({ ...query, page }),
            itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
            totalPages: paginationData.totalPages,
            currentPage: query.page,
          }}
          noResultsText={
            <TableEmptyContent
              title={texts.noRecords}
              subtitle={texts.noRecordsSubtitle}
            />
          }
        />
      </div>
    </>
  );
};
